import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { useSession } from './SessionContext';
import { API_BASE_URL, endpoints } from '../api';
import { getApiKey } from '../utils/auth';
import {
  getBrowserNotificationPermission,
  initializeBrowserNotifications,
  isBackgroundNotificationContext,
  requestBrowserNotificationPermission,
  showBrowserNotification
} from '../utils/browserNotifications';
import { initializeOrderSound, playOrderNotificationSound } from '../utils/orderSound';

const NotificationContext = createContext();

const ORDER_SOUND_EVENT_WINDOW_MS = 10000;
const LIVE_ALERT_DURATION_MS = 6000;

const normalizeValue = (value) => String(value || '').toLowerCase().trim();

const normalizeOrderStatus = (value) => {
  const status = normalizeValue(value);
  return status === 'pending' ? 'placed' : status;
};

const normalizePackageStatus = (value) => normalizeValue(value);

const getStoredAuthToken = () => (
  localStorage.getItem('token') ||
  localStorage.getItem('vendor_token') ||
  localStorage.getItem('adminToken') ||
  localStorage.getItem('admin_token') ||
  ''
);

const getOrderEventKey = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return '';
  }

  return String(
    payload.id ||
    payload.order_id ||
    payload.orderId ||
    payload.order_number ||
    payload.orderNumber ||
    payload.package_id ||
    payload.packageId ||
    payload.delivery_code ||
    payload.tracking_code ||
    payload.notification_id ||
    payload.notificationId ||
    payload.created_at ||
    payload.timestamp ||
    ''
  ).trim();
};

const isPackagePayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const action = normalizeValue(payload.action);
  const event = normalizeValue(payload.event);
  const type = normalizeValue(payload.type);
  const category = normalizeValue(payload.category || payload.notification_type);
  const title = normalizeValue(payload.title);
  const message = normalizeValue(payload.message);

  return Boolean(
    payload.package_id ||
    payload.packageId ||
    payload.delivery_code ||
    payload.tracking_code ||
    payload.package_type ||
    payload.parcel_type ||
    type.includes('package') ||
    category.includes('package') ||
    event.includes('package') ||
    action.includes('package') ||
    title.includes('package') ||
    message.includes('package')
  );
};

const isNewOrderPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const action = normalizeValue(payload.action);
  const event = normalizeValue(payload.event);
  const type = normalizeValue(payload.type);
  const category = normalizeValue(payload.category || payload.notification_type);
  const status = normalizeOrderStatus(payload.status);
  const title = normalizeValue(payload.title);
  const message = normalizeValue(payload.message);
  const hasOrderIdentity = Boolean(
    payload.id ||
    payload.order_id ||
    payload.orderId ||
    payload.order_number ||
    payload.orderNumber
  );
  const isOrderCategory = (
    type.includes('order') ||
    category.includes('order') ||
    event.includes('order') ||
    title.includes('order') ||
    message.includes('order')
  );

  const exactMatches = new Set([
    'created',
    'new_order',
    'new-order',
    'order_created',
    'order-created',
    'order_placed',
    'order-placed'
  ]);

  const containsNewOrderText = (
    title.includes('new order') ||
    message.includes('new order') ||
    message.includes('order placed') ||
    message.includes('new order received')
  );

  if (
    exactMatches.has(action) ||
    exactMatches.has(event) ||
    exactMatches.has(type) ||
    exactMatches.has(category)
  ) {
    return true;
  }

  if (
    action.includes('new_order') ||
    action.includes('order_created') ||
    event.includes('new_order') ||
    event.includes('order_created') ||
    type.includes('new_order') ||
    category.includes('new_order')
  ) {
    return true;
  }

  if (containsNewOrderText) {
    return true;
  }

  if (hasOrderIdentity && isOrderCategory && ['placed', 'confirmed', 'packing', 'ready'].includes(status)) {
    return true;
  }

  return false;
};

const isNewPackagePayload = (payload) => {
  if (!isPackagePayload(payload)) {
    return false;
  }

  const action = normalizeValue(payload.action);
  const event = normalizeValue(payload.event);
  const type = normalizeValue(payload.type);
  const category = normalizeValue(payload.category || payload.notification_type);
  const title = normalizeValue(payload.title);
  const message = normalizeValue(payload.message);
  const status = normalizePackageStatus(
    payload.delivery_status ||
    payload.shipment_status ||
    payload.status
  );

  const exactMatches = new Set([
    'created',
    'new_package',
    'new-package',
    'package_created',
    'package-created',
    'package_order_created',
    'package-order-created',
    'package_order_placed',
    'package-order-placed'
  ]);

  if (
    exactMatches.has(action) ||
    exactMatches.has(event) ||
    exactMatches.has(type) ||
    exactMatches.has(category)
  ) {
    return true;
  }

  if (
    action.includes('new_package') ||
    action.includes('package_created') ||
    event.includes('new_package') ||
    event.includes('package_created') ||
    type.includes('new_package') ||
    category.includes('new_package')
  ) {
    return true;
  }

  if (
    title.includes('new package') ||
    message.includes('new package') ||
    message.includes('package order received') ||
    message.includes('package created')
  ) {
    return true;
  }

  return ['pending', 'placed', 'accepted'].includes(status);
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [browserNotificationPermission, setBrowserNotificationPermission] = useState(
    getBrowserNotificationPermission()
  );
  const { user, isAuthenticated } = useSession();
  const recentOrderEventsRef = useRef(new Map());
  const recentBrowserNotificationsRef = useRef(new Map());
  const liveAlertTimeoutsRef = useRef(new Map());
  const notificationsRef = useRef([]);
  const knownOrdersRef = useRef(new Map());
  const ordersBootstrapDoneRef = useRef(false);
  const knownPackageOrdersRef = useRef(new Map());
  const packageOrdersBootstrapDoneRef = useRef(false);

  const maybePlayNewOrderSound = useCallback((payload) => {
    if (!isNewOrderPayload(payload) && !isNewPackagePayload(payload)) {
      return;
    }

    const now = Date.now();
    const orderKey = getOrderEventKey(payload);
    const recentOrderEvents = recentOrderEventsRef.current;

    recentOrderEvents.forEach((timestamp, key) => {
      if (now - timestamp > ORDER_SOUND_EVENT_WINDOW_MS) {
        recentOrderEvents.delete(key);
      }
    });

    if (orderKey) {
      const lastEventAt = recentOrderEvents.get(orderKey);
      if (lastEventAt && now - lastEventAt < ORDER_SOUND_EVENT_WINDOW_MS) {
        return;
      }
      recentOrderEvents.set(orderKey, now);
    }

    playOrderNotificationSound();
  }, []);

  const maybeShowBrowserNotification = useCallback(async (notification) => {
    if (!notification?.title || !isBackgroundNotificationContext()) {
      return;
    }

    const notificationKey = String(
      notification.id ||
      notification.notification_id ||
      getOrderEventKey(notification.orderData || notification) ||
      `${notification.title}-${notification.message || ''}`
    );
    const now = Date.now();
    const recentBrowserNotifications = recentBrowserNotificationsRef.current;

    recentBrowserNotifications.forEach((timestamp, key) => {
      if (now - timestamp > ORDER_SOUND_EVENT_WINDOW_MS) {
        recentBrowserNotifications.delete(key);
      }
    });

    if (notificationKey) {
      const lastShownAt = recentBrowserNotifications.get(notificationKey);
      if (lastShownAt && now - lastShownAt < ORDER_SOUND_EVENT_WINDOW_MS) {
        return;
      }
      recentBrowserNotifications.set(notificationKey, now);
    }

    await showBrowserNotification({
      title: notification.title,
      body: notification.message,
      tag: `${notification.type || 'notification'}-${notificationKey || notification.title}`,
      data: {
        url: '/admin',
        notificationId: notification.id,
        orderId: notification.orderData?.id || notification.order_id || null,
        packageId: notification.orderData?.package_id || notification.package_id || null,
        targetTab: notification.targetTab || null
      }
    });
  }, []);

  const enableBrowserNotifications = useCallback(async () => {
    const nextPermission = await requestBrowserNotificationPermission();
    setBrowserNotificationPermission(nextPermission);
    return nextPermission;
  }, []);

  const dismissLiveAlert = useCallback((alertId) => {
    const timeoutId = liveAlertTimeoutsRef.current.get(alertId);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      liveAlertTimeoutsRef.current.delete(alertId);
    }
    setLiveAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
  }, []);

  const pushLiveAlert = useCallback((alert) => {
    if (!alert?.id) {
      return;
    }

    setLiveAlerts((prev) => [alert, ...prev.filter((item) => item.id !== alert.id)].slice(0, 4));

    const existingTimeout = liveAlertTimeoutsRef.current.get(alert.id);
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
    }

    const timeoutId = window.setTimeout(() => {
      setLiveAlerts((prev) => prev.filter((item) => item.id !== alert.id));
      liveAlertTimeoutsRef.current.delete(alert.id);
    }, LIVE_ALERT_DURATION_MS);

    liveAlertTimeoutsRef.current.set(alert.id, timeoutId);
  }, []);

  const buildRealtimeNotification = useCallback((payload, fallbackTitle = 'Notification') => {
    const id = String(
      payload?.notification_id ||
      payload?.id ||
      payload?.order_id ||
      payload?.order_number ||
      payload?.package_id ||
      payload?.delivery_code ||
      payload?.tracking_code ||
      `${Date.now()}-${Math.random()}`
    );
    const normalizedStatus = normalizeOrderStatus(payload?.status);
    const normalizedPackageStatus = normalizePackageStatus(
      payload?.delivery_status ||
      payload?.shipment_status ||
      payload?.status
    );
    const packageIdentity = (
      payload?.package_id ||
      payload?.delivery_code ||
      payload?.tracking_code ||
      payload?.order_number
    );
    const packageNotification = isPackagePayload(payload);

    return {
      id,
      title: payload?.title || (
        packageNotification
          ? (packageIdentity ? `Package ${packageIdentity}` : 'Package Update')
          : (payload?.order_number ? `Order ${payload.order_number}` : fallbackTitle)
      ),
      message:
        payload?.message ||
        (
          packageNotification
            ? (
              isNewPackagePayload(payload)
                ? `New package order ${packageIdentity || ''} received`
                : packageIdentity
                  ? `Package ${packageIdentity}${normalizedPackageStatus ? ` updated to ${normalizedPackageStatus}` : ' updated'}`
                  : 'Package updated'
            )
            : (
              payload?.action === 'reassigned'
                ? `Order ${payload.order_number || ''} reassigned`
                : payload?.order_number
                  ? `Order ${payload.order_number}${normalizedStatus ? ` updated to ${normalizedStatus}` : ' updated'}`
                  : fallbackTitle
            )
        ),
      unread: payload?.unread ?? true,
      time: payload?.time || new Date().toLocaleTimeString(),
      created_at: payload?.created_at || new Date().toISOString(),
      type: payload?.type || payload?.notification_type || (packageNotification ? 'package_update' : 'order_update'),
      targetTab: packageNotification ? 'packages' : 'orders',
      orderData: payload
    };
  }, []);

  const normalizeNotification = useCallback((notification) => ({
    ...notification,
    id: String(
      notification?.id ||
      notification?.notification_id ||
      notification?.order_id ||
      notification?.order_number ||
      `${Date.now()}-${Math.random()}`
    ),
    unread: notification?.unread ?? true,
    time: notification?.time || new Date(notification?.created_at || Date.now()).toLocaleTimeString()
  }), []);

  const fetchRecentOrders = useCallback(async () => {
    if (!isAuthenticated || user?.type !== 'admin') {
      return;
    }

    try {
      const apiKey = getApiKey();
      const response = await fetch(`${API_BASE_URL}/api/admin/orders?page=1&limit=20`, {
        headers: {
          'x-admin-key': apiKey
        }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const orders = Array.isArray(data)
        ? data
        : Array.isArray(data?.orders)
          ? data.orders
          : [];

      const nextKnownOrders = new Map();

      orders.forEach((order) => {
        const orderKey = getOrderEventKey(order);
        if (!orderKey) {
          return;
        }

        const normalizedOrder = {
          ...order,
          status: normalizeOrderStatus(order.status)
        };

        const snapshot = JSON.stringify({
          status: normalizedOrder.status,
          payment_status: normalizedOrder.payment_status || '',
          updated_at: normalizedOrder.updated_at || '',
          created_at: normalizedOrder.created_at || ''
        });

        nextKnownOrders.set(orderKey, snapshot);

        if (!ordersBootstrapDoneRef.current) {
          return;
        }

        const previousSnapshot = knownOrdersRef.current.get(orderKey);
        if (previousSnapshot === snapshot) {
          return;
        }

        const nextNotification = buildRealtimeNotification(
          {
            ...normalizedOrder,
            action: previousSnapshot ? 'status_updated' : 'order_created',
            message: previousSnapshot
              ? `Order ${normalizedOrder.order_number} updated to ${normalizedOrder.status}`
              : `New order ${normalizedOrder.order_number} received`
          },
          previousSnapshot ? 'Order Update' : 'New Order'
        );

        setNotifications((prev) => [nextNotification, ...prev]);
        setUnreadCount((prev) => prev + 1);
        pushLiveAlert(nextNotification);
        maybePlayNewOrderSound({
          ...normalizedOrder,
          action: previousSnapshot ? 'status_updated' : 'order_created',
          type: 'order_update',
          message: nextNotification.message
        });
        maybeShowBrowserNotification(nextNotification);
      });

      knownOrdersRef.current = nextKnownOrders;
      ordersBootstrapDoneRef.current = true;
    } catch (error) {
      console.error('Error fetching recent orders:', error);
    }
  }, [
    isAuthenticated,
    user,
    buildRealtimeNotification,
    pushLiveAlert,
    maybePlayNewOrderSound,
    maybeShowBrowserNotification
  ]);

  const fetchRecentPackageOrders = useCallback(async () => {
    if (!isAuthenticated || user?.type !== 'admin') {
      return;
    }

    try {
      const apiKey = getApiKey();
      const response = await fetch(`${API_BASE_URL}${endpoints.packageOrders}?page=1&limit=20`, {
        headers: {
          'x-admin-key': apiKey
        }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const packageOrders = Array.isArray(data)
        ? data
        : Array.isArray(data?.orders)
          ? data.orders
          : Array.isArray(data?.packages)
            ? data.packages
            : Array.isArray(data?.data)
              ? data.data
              : [];

      const nextKnownPackages = new Map();

      packageOrders.forEach((pkg) => {
        const packageKey = getOrderEventKey(pkg);
        if (!packageKey) {
          return;
        }

        const normalizedPackage = {
          ...pkg,
          status: normalizePackageStatus(pkg.delivery_status || pkg.shipment_status || pkg.status)
        };

        const snapshot = JSON.stringify({
          status: normalizedPackage.status,
          updated_at: normalizedPackage.updated_at || '',
          created_at: normalizedPackage.created_at || '',
          partner_id: normalizedPackage.delivery_partner_id || normalizedPackage.partner_id || ''
        });

        nextKnownPackages.set(packageKey, snapshot);

        if (!packageOrdersBootstrapDoneRef.current) {
          return;
        }

        const previousSnapshot = knownPackageOrdersRef.current.get(packageKey);
        if (previousSnapshot === snapshot) {
          return;
        }

        const packageIdentity = (
          normalizedPackage.package_id ||
          normalizedPackage.delivery_code ||
          normalizedPackage.tracking_code ||
          normalizedPackage.order_number
        );

        const nextNotification = buildRealtimeNotification(
          {
            ...normalizedPackage,
            action: previousSnapshot ? 'package_status_updated' : 'package_created',
            type: 'package_update',
            message: previousSnapshot
              ? `Package ${packageIdentity || ''} updated to ${normalizedPackage.status || 'updated'}`
              : `New package order ${packageIdentity || ''} received`
          },
          previousSnapshot ? 'Package Update' : 'New Package Order'
        );

        setNotifications((prev) => [nextNotification, ...prev]);
        setUnreadCount((prev) => prev + 1);
        pushLiveAlert(nextNotification);
        maybePlayNewOrderSound({
          ...normalizedPackage,
          action: previousSnapshot ? 'package_status_updated' : 'package_created',
          type: 'package_update',
          message: nextNotification.message
        });
        maybeShowBrowserNotification(nextNotification);
      });

      knownPackageOrdersRef.current = nextKnownPackages;
      packageOrdersBootstrapDoneRef.current = true;
    } catch (error) {
      console.error('Error fetching recent package orders:', error);
    }
  }, [
    isAuthenticated,
    user,
    buildRealtimeNotification,
    pushLiveAlert,
    maybePlayNewOrderSound,
    maybeShowBrowserNotification
  ]);

  useEffect(() => initializeOrderSound(), []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return () => {};
    }

    const cleanup = initializeBrowserNotifications((nextPermission) => {
      setBrowserNotificationPermission(nextPermission);
    });

    const syncPermissionState = () => {
      setBrowserNotificationPermission(getBrowserNotificationPermission());
    };

    window.addEventListener('focus', syncPermissionState);
    document.addEventListener('visibilitychange', syncPermissionState);

    return () => {
      cleanup();
      window.removeEventListener('focus', syncPermissionState);
      document.removeEventListener('visibilitychange', syncPermissionState);
    };
  }, []);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // Initialize socket connection
  useEffect(() => {
    if (isAuthenticated && user) {
      const apiUrl = API_BASE_URL || process.env.REACT_APP_API_URL || window.location.origin;
      const socketToken = user?.type === 'admin' ? getApiKey() : getStoredAuthToken();
      console.log('🔌 Initializing Socket.IO connection to:', apiUrl);
      
      const newSocket = io(apiUrl, {
        auth: {
          token: socketToken,
          type: user?.type || 'admin'
        },
        transports: ['websocket', 'polling'], // Try websocket first, fallback to polling
        withCredentials: true,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        timeout: 20000,
        path: '/socket.io/'
      });

      newSocket.on('connect', () => {
        console.log('✅ Socket connected successfully!', newSocket.id);
        console.log('   Transport:', newSocket.io.engine.transport.name);
        setIsConnected(true);
        
        // Authenticate with the server
        const token = socketToken;
        if (token) {
          console.log('🔐 Sending authentication...');
          newSocket.emit('authenticate', { token, type: user?.type || 'admin' });
        }

        if (user?.type === 'admin') {
          newSocket.emit('join-admin');
        }
      });

      newSocket.on('disconnect', (reason) => {
        console.log('🔌 Socket disconnected:', reason);
        setIsConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error.message);
        setIsConnected(false);
      });

      newSocket.on('reconnect_attempt', (attemptNumber) => {
        console.log('🔄 Reconnection attempt:', attemptNumber);
      });

      newSocket.on('reconnect', (attemptNumber) => {
        console.log('✅ Socket reconnected after', attemptNumber, 'attempts');
        setIsConnected(true);
      });

      newSocket.on('authenticated', (data) => {
        console.log('✅ Socket authenticated:', data);
      });

      newSocket.on('authentication_error', (error) => {
        console.error('❌ Socket authentication error:', error);
      });

      newSocket.on('new_notification', (notification) => {
        console.log('🔔 New notification received:', notification);
        const nextNotification = buildRealtimeNotification(notification, 'Notification');
        setNotifications(prev => [nextNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
        pushLiveAlert(nextNotification);
        maybePlayNewOrderSound(notification);
        maybeShowBrowserNotification(nextNotification);
      });

      newSocket.on('order-update', (orderData) => {
        console.log('🛒 Order update received:', orderData);
        const nextNotification = buildRealtimeNotification(orderData, 'Order Update');
        setNotifications((prev) => [nextNotification, ...prev]);
        setUnreadCount((prev) => prev + 1);
        pushLiveAlert(nextNotification);
        maybePlayNewOrderSound(orderData);
        maybeShowBrowserNotification(nextNotification);
      });

      setSocket(newSocket);

      return () => {
        console.log('🔌 Closing socket connection');
        newSocket.close();
      };
    }
  }, [
    isAuthenticated,
    user,
    maybePlayNewOrderSound,
    pushLiveAlert,
    buildRealtimeNotification,
    maybeShowBrowserNotification
  ]);

  const fetchNotifications = useCallback(async (options = {}) => {
    try {
      const token = getStoredAuthToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const nextNotifications = (data.notifications || []).map(normalizeNotification);
        const previousNotifications = notificationsRef.current;

        if (options.triggerLive) {
          nextNotifications.forEach((notification) => {
            const seenAlready = previousNotifications.some((prev) => String(prev.id) === String(notification.id));
            if (!seenAlready && notification.unread) {
              pushLiveAlert(notification);
              maybePlayNewOrderSound(notification);
              maybeShowBrowserNotification(notification);
            }
          });
        }

        setNotifications(nextNotifications);
        setUnreadCount(nextNotifications.filter(n => n.unread).length || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [normalizeNotification, pushLiveAlert, maybePlayNewOrderSound, maybeShowBrowserNotification]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = getStoredAuthToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  // Fetch initial notifications
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchNotifications();
      fetchRecentOrders();
      fetchRecentPackageOrders();
    }
  }, [isAuthenticated, user, fetchNotifications, fetchRecentOrders, fetchRecentPackageOrders]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      fetchNotifications({ triggerLive: true });
      fetchRecentOrders();
      fetchRecentPackageOrders();
    }, 8000);

    return () => window.clearInterval(interval);
  }, [isAuthenticated, user, fetchNotifications, fetchRecentOrders, fetchRecentPackageOrders]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const token = getStoredAuthToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, unread: false } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const token = getStoredAuthToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/notifications/mark-all-read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    liveAlerts,
    isConnected,
    browserNotificationPermission,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    dismissLiveAlert,
    enableBrowserNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
